import { useEffect, useState } from "react";
import { ChevronDown, Layers, Target } from "lucide-react";

// Four IB topics only. Activation is server-declared (`signal.card_v2`, derived from the
// owning IB's canonical referral code + internal_broadcast flag). The env id list is a
// development-only override for local fixtures and is ignored in production builds.
const V2_TOPIC_IDS = new Set(
  (process.env.NODE_ENV === "development" ? process.env.REACT_APP_IB_CARD_V2_TOPIC_IDS || "" : "").split(",").map((s) => s.trim()).filter(Boolean),
);

export const isIbCardV2Topic = (signal) => !!signal && (signal.card_v2 === true || V2_TOPIC_IDS.has(String(signal.id)));

// Reliable parent linkage only: a shared setup_event_id exposed on both messages.
const setupKey = (m) => m?.card_data?.setup_event_id || m?.setup_event_id || null;
const isEntry = (m) => m?.card_data?.kind === "signal";
const isUpdate = (m) => m?.card_data?.kind === "tp_result";

// Rendering-only grouping over unchanged messages. Unlinked updates stay top-level.
export function groupIbSignalItems(items) {
  const entryByKey = new Map();
  for (const m of items) { const k = setupKey(m); if (isEntry(m) && k && !entryByKey.has(k)) entryByKey.set(k, m.id); }
  const updates = new Map();
  const nested = new Set();
  for (const m of items) {
    const k = setupKey(m);
    if (isUpdate(m) && k && entryByKey.has(k)) {
      const pid = entryByKey.get(k);
      if (!updates.has(pid)) updates.set(pid, []);
      updates.get(pid).push(m); nested.add(m.id);
    }
  }
  return items.filter((m) => !nested.has(m.id)).map((m) => ({ m, updates: updates.get(m.id) || [] }));
}

const Row = ({ label, value, testId, red }) => (
  <div className="flex items-baseline justify-between py-1.5">
    <span className="ibv2-label">{label}</span>
    <span className={`ibv2-value ${red ? "text-[#FF4D5A]" : ""}`} data-testid={testId}>{value}</span>
  </div>
);

export function IbUpdateCardV2({ c, nested = false }) {
  return (
    <div className={`ibv2-update ${nested ? "is-nested" : ""}`} data-testid="signal-tp-card">
      <div className="flex items-baseline justify-between gap-3">
        <span className="ibv2-update__title" data-testid="signal-tp-stage">{c.indicator === "KLER" ? "TP HIT" : `HIT ${c.stage}`}</span>
        {!nested && <span className="text-[11px] text-white/60">{c.symbol} · {c.timeframe}</span>}
      </div>
      <div className="ibv2-update__pips" data-testid="signal-tp-pips">{c.pips} PIPS</div>
      <div className="ibv2-update__target">
        <Target className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
        <span className="text-[11px] tracking-widest uppercase text-white/70">{c.target_label}</span>
        <span className="ml-auto text-sm font-bold text-white tabular-nums" data-testid="signal-tp-target">{c.target}</span>
      </div>
    </div>
  );
}

export function IbEntryCardV2({ c, mid, updates = [], renderUpdate, focusId = null }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!focusId) return;
    setOpen(true);
    const t = setTimeout(() => document.querySelector(`[data-testid="signal-msg-${focusId}"]`)?.scrollIntoView({ block: "center" }), 60);
    return () => clearTimeout(t);
  }, [focusId]);
  const last = updates[updates.length - 1];
  const panelId = `ibv2-updates-${mid}`;
  return (
    <div className="ibv2-card w-full" data-testid="signal-setup-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className={`mt-1.5 h-3 w-3 rounded-full shrink-0 ${c.accent === "red" ? "bg-[#FF4D5A]" : "bg-emerald-400"}`} />
          <div className="min-w-0">
            <div className="text-[17px] font-bold tracking-wide text-white uppercase leading-tight" data-testid="signal-setup-title">{c.title}</div>
            <div className="text-[12px] text-white/60 mt-0.5">{c.symbol} · {c.timeframe}</div>
          </div>
        </div>
      </div>
      <div className="ibv2-divider my-2.5" />
      {c.entry != null && <Row label="ENTRY" value={c.entry} testId="signal-setup-entry" />}
      {c.zone != null && <Row label="ZONE" value={c.zone} testId="signal-setup-zone" />}
      {c.sl_text && <Row label="SL" value={c.sl_text} testId="signal-setup-sl" />}
      {(c.tp1 != null || c.tp2 != null) && (
        c.tp2 == null ? (
          <div className="ibv2-tps grid grid-cols-1 mt-2">
            <div className="text-center py-2"><div className="ibv2-tps__label">TP</div><div className="ibv2-tps__value" data-testid="signal-setup-tp1">{c.tp1}</div></div>
          </div>
        ) : (
        <div className="ibv2-tps grid grid-cols-2 mt-2">
          <div className="text-center py-2"><div className="ibv2-tps__label">TP1</div><div className="ibv2-tps__value" data-testid="signal-setup-tp1">{c.tp1 ?? "—"}</div></div>
          <div className="text-center py-2"><div className="ibv2-tps__label">TP2</div><div className="ibv2-tps__value" data-testid="signal-setup-tp2">{c.tp2 ?? "—"}</div></div>
        </div>
        )
      )}
      {c.disclaimer && <div className="text-center text-[11px] text-white/55 mt-2.5">{c.disclaimer}</div>}
      {updates.length > 0 && (
        <>
          <div className="ibv2-divider my-3" />
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 text-sm text-white/85" data-testid={`ibv2-count-${mid}`}><Layers className="h-4 w-4 text-white/60" />{updates.length} {updates.length === 1 ? "update" : "updates"}</span>
            {last && <span className="ibv2-badge" data-testid={`ibv2-badge-${mid}`}>{last.card_data.indicator === "KLER" ? "TP" : last.card_data.stage} HIT</span>}
          </div>
          <div id={panelId} role="region" hidden={!open} className="ibv2-timeline mt-3" data-testid={`ibv2-panel-${mid}`}>
            {updates.map((u) => <div key={u.id} className="ibv2-timeline__item">{renderUpdate(u)}</div>)}
          </div>
          <button type="button" className="ibv2-toggle mt-3" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)} data-testid={`ibv2-toggle-${mid}`}>
            {open ? "Hide Updates" : "View Updates"}<ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
        </>
      )}
    </div>
  );
}
